/**
 * The enterprise-asset-management module's route table (C11 §10).
 *
 * `src/router/index.ts` is shared by every module, so this file declares the
 * records and the router spreads `EAM_ROUTES` in.
 *
 * Three decisions are worth stating:
 *
 *  - Every `:id` is constrained to `\d+`, so `/eam/assets/reconciliation` can
 *    never be parsed as a record id, and the fixed path is declared first so the
 *    ordering says what the constraint enforces.
 *  - **There is no permit *list* screen separate from the board.** A permit is a
 *    thing three different people act on in sequence, and a list that only
 *    showed rows would leave each of them hunting for their own step. The board
 *    is the list.
 *  - **The financial asset register is not re-exposed here.** `internal/assets`
 *    owns it and `/assets/register` already edits it; the reconciliation report
 *    links across rather than offering a second way to open the same row. Two
 *    screens over one table is the drift this module's own doc comment refuses.
 */

import type { RouteRecordRaw } from 'vue-router'

import { EAM_PERMISSIONS } from '@/modules/eam/permissions'

export const EAM_ROUTES: RouteRecordRaw[] = [
  {
    path: '/eam/locations',
    name: 'eam-locations',
    component: () => import('@/modules/eam/FunctionalLocationsView.vue'),
    meta: { titleKey: 'eam.routeTitles.locations', permission: EAM_PERMISSIONS.assetRead },
  },
  {
    path: '/eam/assets',
    name: 'eam-assets',
    component: () => import('@/modules/eam/AssetsListView.vue'),
    meta: { titleKey: 'eam.routeTitles.assets', permission: EAM_PERMISSIONS.assetRead },
  },
  {
    path: '/eam/assets/reconciliation',
    name: 'eam-reconciliation',
    component: () => import('@/modules/eam/ReconciliationView.vue'),
    meta: { titleKey: 'eam.routeTitles.reconciliation', permission: EAM_PERMISSIONS.reportRead },
  },
  {
    path: '/eam/assets/:id(\\d+)',
    name: 'eam-asset',
    component: () => import('@/modules/eam/AssetView.vue'),
    meta: { titleKey: 'eam.routeTitles.asset', permission: EAM_PERMISSIONS.assetRead },
  },
  {
    path: '/eam/maintenance',
    name: 'eam-maintenance',
    component: () => import('@/modules/eam/MaintenanceView.vue'),
    meta: { titleKey: 'eam.routeTitles.maintenance', permission: EAM_PERMISSIONS.planRead },
  },
  {
    path: '/eam/task-lists/:id(\\d+)',
    name: 'eam-task-list',
    component: () => import('@/modules/eam/TaskListView.vue'),
    meta: { titleKey: 'eam.routeTitles.taskList', permission: EAM_PERMISSIONS.planRead },
  },
  {
    path: '/eam/meters',
    name: 'eam-meters',
    component: () => import('@/modules/eam/MetersView.vue'),
    meta: { titleKey: 'eam.routeTitles.meters', permission: EAM_PERMISSIONS.meterRead },
  },
  {
    path: '/eam/failures',
    name: 'eam-failures',
    component: () => import('@/modules/eam/FailuresView.vue'),
    meta: { titleKey: 'eam.routeTitles.failures', permission: EAM_PERMISSIONS.assetRead },
  },
  {
    path: '/eam/compliance',
    name: 'eam-compliance',
    component: () => import('@/modules/eam/ComplianceCalendarView.vue'),
    meta: { titleKey: 'eam.routeTitles.compliance', permission: EAM_PERMISSIONS.complianceRead },
  },
  {
    path: '/eam/permits',
    name: 'eam-permits',
    component: () => import('@/modules/eam/PermitBoardView.vue'),
    meta: { titleKey: 'eam.routeTitles.permits', permission: EAM_PERMISSIONS.permitRead },
  },
  {
    path: '/eam/downtime',
    name: 'eam-downtime',
    component: () => import('@/modules/eam/DowntimeView.vue'),
    meta: { titleKey: 'eam.routeTitles.downtime', permission: EAM_PERMISSIONS.assetRead },
  },
]

/* ------------------------------------------------------------ own screens */

export const EAM_PATHS = {
  locations: '/eam/locations',
  assets: '/eam/assets',
  asset: (id: number) => `/eam/assets/${id}`,
  reconciliation: '/eam/assets/reconciliation',
  maintenance: '/eam/maintenance',
  taskList: (id: number) => `/eam/task-lists/${id}`,
  meters: '/eam/meters',
  failures: '/eam/failures',
  compliance: '/eam/compliance',
  permits: '/eam/permits',
  downtime: '/eam/downtime',
} as const

/** One asset's meters — the hop from the 360 to the reading grid. */
export function metersForAsset(assetId: number): string {
  return `${EAM_PATHS.meters}?assetId=${assetId}`
}

/** One asset's compliance requirements, from its 360. */
export function complianceForAsset(assetId: number): string {
  return `${EAM_PATHS.compliance}?assetId=${assetId}`
}

/** The open permits against one asset. */
export function permitsForAsset(assetId: number): string {
  return `${EAM_PATHS.permits}?assetId=${assetId}`
}

/* --------------------------------------------------------- links elsewhere */

/**
 * The FINANCIAL asset this one is reconciled against. `internal/assets` owns
 * this URL, and the reconciliation report is the only screen that follows it.
 */
export function financeAssetRoute(financeAssetId: number): string {
  return `/assets/register/${financeAssetId}`
}

/** The work order a PM plan, a compliance failure or a downtime raised. */
export function workOrderRoute(workOrderId: number): string {
  return `/service/work-orders/${workOrderId}`
}
